import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { useSearchFilterStore } from '@/entities/map'
import { RoomSortOption } from '@/entities/room'
import { cn } from '@/shared/lib'
import { Drawer, Icon, TextButton } from '@/shared/ui'

import { useSortOptionLabelMap } from './options'

type Props = {
  value: RoomSortOption
}

export const MobileOrderFilter = ({ value }: Props) => {
  const [selectedValue, setSelectedValue] = useState(value)

  const { SORT_OPTION_LABEL_MAP } = useSortOptionLabelMap()

  useEffect(() => {
    setSelectedValue(value)
  }, [value])

  return (
    <Drawer.Root>
      <Drawer.Trigger asChild>
        <TextButton>
          {SORT_OPTION_LABEL_MAP[selectedValue]}
          <Icon name="arrow-bottom" size={16} className="size-4 text-black" />
        </TextButton>
      </Drawer.Trigger>

      <Drawer.Content>
        <Content value={selectedValue} updateValue={setSelectedValue} />
      </Drawer.Content>
    </Drawer.Root>
  )
}

type ContentProps = {
  value: RoomSortOption
  updateValue: (value: RoomSortOption) => void
}

const Content = ({ value: selectedValue, updateValue }: ContentProps) => {
  const t = useTranslations('search_result.result.sort')

  const filterActions = useSearchFilterStore(state => state.actions)

  const { SORT_OPTION_LABEL_MAP } = useSortOptionLabelMap()

  const selected = (value: RoomSortOption) => {
    return value === selectedValue
  }

  const handleClick = (value: RoomSortOption) => () => {
    updateValue(value)
    filterActions.updateSort(value)
  }

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-hidden px-4 pb-6">
      <div className="flex flex-1 flex-col overflow-hidden">
        <Drawer.Header className="pb-5">
          <Drawer.Title>{t('title')}</Drawer.Title>
          <Drawer.Description className="sr-only">
            {t('description')}
          </Drawer.Description>
        </Drawer.Header>

        <ul>
          {Object.entries(SORT_OPTION_LABEL_MAP).map(([key, value]) => (
            <li
              key={key}
              className="group not-last:border-b not-last:border-gray-50"
            >
              <Drawer.Close
                className="flex w-full justify-between py-4 group-first:pt-0 group-last:pb-0"
                onClick={handleClick(key as RoomSortOption)}
              >
                <span className="typo-body1-reading font-medium text-black">
                  {value}
                </span>
                <div
                  className={cn(
                    'flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full bg-gray-200',
                    'transition-all duration-200',
                    {
                      'bg-black': selected(key as RoomSortOption),
                    },
                  )}
                >
                  <Icon
                    name="check"
                    size={12}
                    strokeWidth={1.5}
                    className="text-white"
                  />
                </div>
              </Drawer.Close>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
